/**
 * assetType
 *
 * Single source of truth for "what kind of thing is this Asset row?". The
 * /my asset query returns a heterogeneous bucket (Application + Asset +
 * WebApplication + IP-class assets + cloud resources etc.) and the table,
 * drawer header, and any future labelling all need the same answer.
 *
 * Deliberately dumb: checks `asset.class` and the key prefix. No network
 * heuristics, no regex on `identifier`. If you need richer detection
 * (e.g. distinguishing AWS vs Azure cloud resources), build on top of
 * this — don't bury it here.
 *
 * @see rollup-frontend-plan.md item F
 */
import type { Asset } from '@/types';

export type AssetTypeKind =
  | 'application'
  | 'webapplication'
  | 'ip'
  | 'domain'
  | 'other';

/** Stable display label for {@link AssetTypeKind}. */
export const ASSET_TYPE_LABEL: Record<AssetTypeKind, string> = {
  application: 'Application',
  webapplication: 'Web Application',
  ip: 'IP',
  domain: 'Domain',
  other: 'Other',
};

/**
 * Acronyms that should stay fully uppercased when prettifying a class.
 * Compared case-insensitively against each whole word after camel-case
 * splitting, so 'VPCEndpoint' → 'VPC Endpoint' and 'cidr' → 'CIDR'.
 */
const CLASS_ACRONYMS = new Set([
  'acl',
  'api',
  'aws',
  'cidr',
  'db',
  'dns',
  'ec2',
  'ecr',
  'ecs',
  'eks',
  'elb',
  'gcp',
  'iam',
  'ip',
  'k8s',
  'rds',
  's3',
  'sns',
  'sql',
  'sqs',
  'tld',
  'vm',
  'vpc',
]);

/** Split a camelCase/PascalCase token into words ('VPCEndpoint' → ['VPC', 'Endpoint']). */
function splitCamelCase(token: string): string[] {
  return token
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .filter(Boolean);
}

function titleCaseWord(word: string): string {
  if (CLASS_ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Human-readable form of a raw asset `class`. Handles the cloud resource
 * type shapes the backend emits (cloud_resource_type.go):
 *
 *  - CloudFormation: `AWS::IAM::Group` → "IAM Group"
 *  - Azure ARM:      `Microsoft.Web/sites/connectionStrings` → "Sites Connection Strings"
 *  - GCP:            `iam.googleapis.com/ServiceAccount` → "Service Account"
 *  - Kubernetes:     `k8s.io/Deployment` → "Deployment"
 *  - Plain classes:  `cidr` → "CIDR", `repository` → "Repository"
 *
 * The leading provider segment (`AWS`, `Microsoft.*`, `*.googleapis.com`,
 * `k8s.io`) is dropped — the remaining segments identify the resource.
 */
export function prettifyAssetClass(klass: string): string {
  let segments = klass.split(/::|\//).filter(Boolean);
  if (segments.length > 1) {
    const provider = segments[0];
    if (provider === 'AWS' || provider.includes('.')) {
      segments = segments.slice(1);
    }
  }
  return segments.flatMap(splitCamelCase).map(titleCaseWord).join(' ');
}

/**
 * Display label for an asset row's type. The categorical kinds use their
 * stable labels; rows that fall into the 'other' bucket render their
 * prettified `class` when present (e.g. cloud resources carry
 * CloudFormation-style classes like `AWS::IAM::Group` → "IAM Group") so
 * heterogeneous rows aren't all flattened to a meaningless "Other".
 */
export function assetTypeLabel(asset: Pick<Asset, 'class' | 'key'>): string {
  const kind = assetType(asset);
  if (kind === 'other' && asset.class) {
    return prettifyAssetClass(asset.class);
  }
  return ASSET_TYPE_LABEL[kind];
}

/**
 * Return the categorical type of an asset row.
 *
 * Order of checks matters: Application is checked first because its key
 * prefix (`#application#`) and `class === 'application'` are both
 * authoritative — every other branch is below it in priority.
 */
export function assetType(asset: Pick<Asset, 'class' | 'key'>): AssetTypeKind {
  const key = asset.key || '';
  const klass = (asset.class || '').toLowerCase();

  if (klass === 'application' || key.startsWith('#application#')) {
    return 'application';
  }
  if (key.startsWith('#webapplication#')) {
    return 'webapplication';
  }
  if (klass === 'ipv4' || klass === 'ipv6') {
    return 'ip';
  }
  if (klass === 'domain' || klass === 'fqdn') {
    return 'domain';
  }
  return 'other';
}

/**
 * The key of the parent :Application whose rollup view a drawer should
 * surface for this asset, or `undefined` when the asset neither *is* nor
 * *rolls up to* an Application.
 *
 * Two distinct cases the drawer must handle — and why `group_key` alone is
 * not enough:
 *
 *  - **The asset IS the Application** (the common path: clicking a member row
 *    resolves *up* to its parent, so the drawer re-fetches the
 *    `:Application` node by key). The `:Application` model embeds only
 *    `BaseAsset` + `FQDN` — it does NOT embed `model.GroupMember`, so it
 *    carries **no `group_key`**. Keying off `group_key` here would wrongly
 *    suppress the rollup view on the primary path. Use the asset's own key.
 *
 *  - **The asset is a member observation** (drilled into directly, e.g. from
 *    the Application drawer's members list, where resolve-up is disabled).
 *    Here `group_key` is the parent's `#application#…` key written onto the
 *    member at observation-write time. Use it.
 *
 * Returns `undefined` for orphan observations (no parent) so callers can gate
 * tab visibility / query-`enabled` on a single truthy check.
 *
 * @see assetType (the application-identity primitive this builds on)
 */
export function applicationRollupKey(
  asset: Pick<Asset, 'class' | 'key' | 'group_key'>
): string | undefined {
  if (assetType(asset) === 'application') {
    return asset.key || undefined;
  }
  return asset.group_key || undefined;
}
